// "Connect an app" (/connect): where a person says yes or no to an AI app asking to see their
// results. The app sends the browser here from the API with #request=<handle>; the page looks
// the request up, shows who is asking and what they would see, and the person picks Allow or
// Cancel. The API decides what is actually granted: this page only shows it.
//
// The handle is kept out of the address bar and out of every URL: it travels only in the
// bodies of the lookup, approve and deny calls. It is stored (localStorage, 15 minutes, used
// once) only when the person follows the link to the staff sign-in or the two-step page from
// here, so the page that comes back after that round trip (the emailed sign-in link opens a new
// tab) can resume it. A visit with the handle in the address is never stored.

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { AppHost } from "../components/ConnectedApps";
import { Loading, Page } from "../components/Page";
import { FacetMark } from "../components/Wordmark";
import { ApiError, api } from "../lib/api";
import { forgetRequestHandle, hideRequestHandle, httpsLogo, keepRequestHandle, safeRedirect, takeRequest } from "../lib/connect";
import type { ConsentLookup, ScopeInfo } from "../lib/types";
import { PersonSignInForm } from "./Account";

type Kind = "staff" | "person";

type Phase =
  | { kind: "loading" }
  | { kind: "ready"; data: ConsentLookup }
  | { kind: "dead"; title: string; body: string }
  | { kind: "failed" };

const ADMIN_BACK = "/admin?next=/connect";
const ADMIN_SIGN_IN = "/admin/sign-in?next=/connect";

const BACK_TO_APP = "Close this tab and go back to the app to try again.";

const DEAD_ENDS: Record<string, { title: string; body: string }> = {
  unknown_client: { title: "This app couldn't be verified", body: `Nothing was shared. ${BACK_TO_APP}` },
  bad_redirect: { title: "This app's sign-in link isn't set up right", body: `Nothing was shared. ${BACK_TO_APP}` },
};
const UNKNOWN_ERROR = { title: "Something went wrong with this app's sign-in", body: `Nothing was shared. ${BACK_TO_APP}` };
const EXPIRED = { title: "This request has expired", body: "Go back to the app and try again." };

export function Connect() {
  const [params] = useSearchParams();
  const errorParam = params.get("error");
  // Read once. The address bar is cleaned right after, and the handle lives on in memory.
  const [taken] = useState(() => (errorParam ? null : takeRequest()));
  const handle = taken?.handle ?? null;
  const [phase, setPhase] = useState<Phase>(() => {
    // hasOwn, not a plain lookup: ?error=constructor must not find an inherited object member.
    if (errorParam) return { kind: "dead", ...(Object.hasOwn(DEAD_ENDS, errorParam) ? DEAD_ENDS[errorParam]! : UNKNOWN_ERROR) };
    if (!handle) return { kind: "dead", title: "Nothing to connect", body: "Open this page from the app you want to connect. It sends you here when it's ready. If you came from an emailed sign-in link, go back to the app and start connecting again." };
    return { kind: "loading" };
  });

  useEffect(() => {
    hideRequestHandle();
    if (errorParam) forgetRequestHandle();
  }, [errorParam]);

  const load = useCallback(async () => {
    if (!handle) return;
    try {
      setPhase({ kind: "ready", data: await api.consentLookup(handle) });
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        forgetRequestHandle();
        setPhase({ kind: "dead", ...EXPIRED });
      } else {
        setPhase({ kind: "failed" });
      }
    }
  }, [handle]);

  useEffect(() => {
    if (handle) void load();
  }, [handle, load]);

  if (phase.kind === "loading") return <Loading label="Checking this request" />;
  if (phase.kind === "dead") return <DeadEnd title={phase.title} body={phase.body} />;
  if (phase.kind === "failed") {
    return (
      <DeadEnd title="We couldn't load this request" body="Check your connection, then try again.">
        <button
          type="button"
          className="btn btn-secondary mt-6"
          onClick={() => {
            setPhase({ kind: "loading" });
            void load();
          }}
        >
          Try again
        </button>
      </DeadEnd>
    );
  }
  return <Consent handle={handle!} resumed={!!taken?.resumed} data={phase.data} reload={load} />;
}

/** Puts focus on the heading when a screen appears, so a screen reader starts there. */
function useHeadingFocus() {
  const ref = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return ref;
}

function DeadEnd({ title, body, children }: { title: string; body: string; children?: ReactNode }) {
  const heading = useHeadingFocus();
  return (
    <Page>
      <div className="mt-16">
        <h1 ref={heading} tabIndex={-1} className="heading text-[30px] leading-[1.1] outline-none">
          {title}
        </h1>
        <p className="mt-4 text-[17px] leading-relaxed text-muted">{body}</p>
        {children}
        <p className="mt-6 text-[15px]">
          <a href="/">Go to Gifts Profile</a>
        </p>
      </div>
    </Page>
  );
}

function AppLogo({ name, uri }: { name: string; uri: string | null }) {
  const [broken, setBroken] = useState(false);
  const src = httpsLogo(uri);
  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        width={56}
        height={56}
        referrerPolicy="no-referrer"
        className="h-14 w-14 shrink-0 rounded-full border border-line bg-panel object-cover"
        onError={() => setBroken(true)}
      />
    );
  }
  // No usable logo: the app's first letter in a circle.
  const initial = Array.from(name.trim().toUpperCase()).find((ch) => /[\p{L}\p{N}]/u.test(ch)) ?? "?";
  return (
    <span aria-hidden="true" className="heading grid h-14 w-14 shrink-0 place-items-center rounded-full bg-soft text-[24px] leading-none text-ink">
      {initial}
    </span>
  );
}

const KIND_LABEL: Record<Kind, string> = { person: "Your Gifts Profile results", staff: "Staff" };

/** What each permission lets the app do, said as an action (the API's own words are nouns). */
const CAN: Record<string, string> = {
  "results:own": "See your own results",
  "results:team": "See the results of people on teams you lead",
  "results:all": "See every result",
  "stats:read": "See stats",
  "site:read": "See campaigns and funnel stats",
  "site:write": "Change funnels and pages",
  "sales:write": "Make discount codes (each with a use limit and an end date), personal test links, and free-test codes (25 a day)",
  offline_access: "Stay connected until you disconnect it",
};

function Bullets({ scopes, id, app }: { scopes: ScopeInfo[]; id: string; app: string }) {
  return (
    <div id={id}>
      <p className="text-[15px] font-semibold">{app} will be able to:</p>
      <ul className="mt-3 flex flex-col gap-2.5 text-[15px] leading-snug">
        {scopes.map((s) => (
          <li key={s.scope} className="flex items-start gap-2.5">
            <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true" className="mt-[1px] shrink-0 text-accent-text">
              <path d="M5 10.5l3.2 3.2L15 6.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{CAN[s.scope] ?? s.description}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The two marks, joined: the app on the left, Gifts Profile on the right. */
function Joined({ name, uri }: { name: string; uri: string | null }) {
  return (
    <div className="flex items-center justify-center gap-3" aria-hidden="true">
      <AppLogo name={name} uri={uri} />
      <span className="flex items-center gap-1.5 text-muted">
        <span className="h-1.5 w-1.5 rounded-full bg-line" />
        <span className="h-1.5 w-1.5 rounded-full bg-muted/50" />
        <span className="h-1.5 w-1.5 rounded-full bg-line" />
      </span>
      <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full border border-line bg-panel">
        <FacetMark size={30} />
      </span>
    </div>
  );
}

/** Initial in a circle, for the signed-in account. */
function Initial({ email }: { email: string }) {
  return (
    <span aria-hidden="true" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-soft text-[16px] font-bold text-ink">
      {email.trim().charAt(0).toUpperCase() || "?"}
    </span>
  );
}

function Consent({ handle, resumed, data, reload }: { handle: string; resumed: boolean; data: ConsentLookup; reload: () => Promise<void> }) {
  const heading = useHeadingFocus();
  const choiceId = useId();
  const { client, accounts } = data;
  const [chosen, setChosen] = useState<Kind | null>(null);
  const [sending, setSending] = useState<"allow" | "deny" | null>(null);
  const [done, setDone] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [needsMfa, setNeedsMfa] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const staff = accounts.staff;
  const person = accounts.person;
  const staffNeedsMfa = !!staff && (!staff.mfaPassed || needsMfa);
  // Accounts that could actually give this app something right now.
  const staffOk = !!staff && !staffNeedsMfa && staff.scopes.length > 0;
  const personOk = !!person && person.scopes.length > 0;
  const options: Kind[] = [...(staffOk ? (["staff"] as const) : []), ...(personOk ? (["person"] as const) : [])];
  const effective: Kind | null = options.length === 1 ? options[0]! : chosen && options.includes(chosen) ? chosen : null;
  const signedIn = !!staff || !!person;

  const act = async (what: "allow" | "deny") => {
    if (sending || done) return;
    setMessage(null);
    setSending(what);
    try {
      const r = what === "allow" ? await api.consentApprove(handle, effective!, data.csrf) : await api.consentDeny(handle, data.csrf);
      // Answered: the handle is spent, so don't keep it.
      forgetRequestHandle();
      if (!safeRedirect(r.redirectTo)) {
        setSending(null);
        setMessage("We couldn't send you back to the app. Go back to it and try again.");
        return;
      }
      setDone(true);
      window.location.assign(r.redirectTo);
    } catch (e) {
      setSending(null);
      const code = e instanceof ApiError ? e.code : "";
      const status = e instanceof ApiError ? e.status : 0;
      if (status === 404) {
        forgetRequestHandle();
        setMessage("This request has expired. Go back to the app and try again.");
      } else if (code === "mfa_required" || code === "mfa_enrollment_required") {
        setNeedsMfa(true);
        setChosen(null);
      } else if (code === "no_scopes") {
        setMessage("This account can't give this app any of the access it asked for.");
      } else if (code === "bad_csrf" || code === "bad_origin") {
        setMessage("This page was out of date, so we refreshed it. Press the button again.");
        void reload();
      } else if (status === 401) {
        setMessage("You've been signed out. Sign in again to continue.");
        void reload();
      } else {
        setMessage("That didn't work. Check your connection and try again.");
      }
    }
  };

  // The staff sign-in and the two-step page are another trip: keep the request for it (once).
  const leaveForAdmin = { onClick: () => keepRequestHandle(handle), onAuxClick: () => keepRequestHandle(handle) };

  // Someone else's account is signed in on this browser: sign it out and look the request up
  // again, so the page asks who is signing in.
  const signOutPerson = async () => {
    setMessage(null);
    setSigningOut(true);
    try {
      await api.signOut();
    } catch {
      setSigningOut(false);
      setMessage("We couldn't sign you out. Check your connection and try again.");
      return;
    }
    await reload();
    setSigningOut(false);
  };

  const canAllow = effective !== null && !sending && !done && !signingOut;

  return (
    <Page>
      <div className="mx-auto mt-4 w-full max-w-[460px] sm:mt-10">
        {/* A card from tablet width up; on a phone the page itself is the card, so nothing is squeezed. */}
        <div className="py-4 sm:rounded-[24px] sm:border sm:border-line sm:bg-panel sm:px-8 sm:py-9">
          <Joined name={client.name} uri={client.logoUri} />
          <h1 ref={heading} tabIndex={-1} className="heading mt-6 text-center text-[24px] leading-[1.15] outline-none sm:text-[26px]">
            {client.name} <span className="text-muted">({client.host})</span> wants to connect to your Gifts Profile account
          </h1>
          <p className="mt-2 text-center text-[15px]">
            <AppHost host={client.host} verified={client.verified} />
          </p>
          {client.lookalike ? (
            <p className="mt-3 text-center text-[14px] font-semibold leading-snug text-danger" data-testid="lookalike">
              This app&rsquo;s name looks like a well-known app, but it isn&rsquo;t from that app&rsquo;s website. Only continue if you trust {client.host}.
            </p>
          ) : null}
          {client.verified ? null : (
            <p className="mt-3 text-center text-[14px] leading-snug text-muted" data-testid="unverified">
              This app named itself; check the address before you continue.
            </p>
          )}
          {resumed ? (
            <p className="mt-3 text-center text-[14px] leading-snug text-muted" data-testid="resumed">
              Picking up where you left off with {client.name}.
            </p>
          ) : null}

          {!signedIn ? (
            <SignInStep
              // Signing in changes the page's check value, so ask again rather than reuse it.
              onSignedIn={() => void reload()}
              adminLink={leaveForAdmin}
            />
          ) : (
            <div className="mt-7 flex flex-col gap-5 border-t border-line pt-6">
              {staffNeedsMfa && staff ? <MfaNotice email={staff.email} link={leaveForAdmin} /> : null}
              {options.length > 1 ? (
                <fieldset>
                  <legend className="text-[15px] font-semibold">Which account do you want to connect?</legend>
                  <div className="mt-3 flex flex-col gap-3">
                    {options.map((kind) => {
                      const a = kind === "staff" ? staff! : person!;
                      const hid = `${choiceId}-${kind}-h`;
                      const bid = `${choiceId}-${kind}-b`;
                      return (
                        <label
                          key={kind}
                          className="flex cursor-pointer gap-3 rounded-[16px] border border-line bg-panel p-4 has-[:checked]:border-accent has-[:checked]:bg-soft has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent"
                        >
                          <input
                            type="radio"
                            name="account"
                            value={kind}
                            className="mt-1 h-5 w-5 shrink-0 accent-accent-text"
                            checked={chosen === kind}
                            onChange={() => setChosen(kind)}
                            aria-labelledby={hid}
                            aria-describedby={bid}
                          />
                          <span className="min-w-0 flex-1">
                            <span id={hid} className="block">
                              <span className="block text-[16px] font-semibold">{KIND_LABEL[kind]}</span>
                              <span className="block break-all text-[14px] text-muted">{a.email}</span>
                            </span>
                            <span className="mt-4 block">
                              <Bullets id={bid} scopes={a.scopes} app={client.name} />
                            </span>
                            {kind === "person" ? <PersonNote person={accounts.person!} /> : null}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>
              ) : (
                options.map((kind) => {
                  const a = kind === "staff" ? staff! : person!;
                  return (
                    <section key={kind} aria-label="Account to connect" className="flex flex-col gap-5">
                      <div className="flex items-center gap-3">
                        <Initial email={a.email} />
                        <div className="min-w-0">
                          <p className="text-[15px] font-semibold [overflow-wrap:anywhere]">{a.email}</p>
                          <p className="text-[14px] text-muted">Signed in · {KIND_LABEL[kind]}</p>
                        </div>
                      </div>
                      <div className="rounded-[16px] bg-soft p-4">
                        <Bullets id={`${choiceId}-${kind}-b`} scopes={a.scopes} app={client.name} />
                      </div>
                      {kind === "person" ? <PersonNote person={accounts.person!} /> : null}
                    </section>
                  );
                })
              )}
              {person ? (
                <p className="-mt-1 text-[14px] text-muted">
                  Not {staff || options.length > 1 ? person.email : "you"}?{" "}
                  <button type="button" className="underline" disabled={signingOut || !!sending || done} onClick={() => void signOutPerson()}>
                    {signingOut ? "Signing out…" : "Sign out"}
                  </button>
                </p>
              ) : null}
              {/* An account is signed in but has nothing to give this app. */}
              {person && !personOk ? <NothingToGive email={person.email} /> : null}
              {staff && !staffNeedsMfa && !staffOk ? <NothingToGive email={staff.email} /> : null}
              {!staff ? (
                <p className="text-[14px] text-muted">
                  Gifts Profile staff? <a href={ADMIN_SIGN_IN} {...leaveForAdmin}>Sign in to the admin</a>
                </p>
              ) : null}
            </div>
          )}

          <div aria-live="polite" className="mt-4 text-[15px] empty:mt-0">
            {message ? <p className="text-danger">{message}</p> : null}
            {done ? <p className="text-muted">Taking you back to {client.redirectHost}…</p> : null}
            {options.length > 1 && !effective && !message && !done ? <p className="text-muted">Choose an account to continue.</p> : null}
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row-reverse">
            {signedIn ? (
              <button type="button" className="btn btn-primary w-full sm:flex-1" disabled={!canAllow} onClick={() => void act("allow")}>
                {sending === "allow" ? "Connecting…" : "Allow"}
              </button>
            ) : null}
            <button type="button" className="btn btn-secondary w-full sm:flex-1" disabled={!!sending || done} onClick={() => void act("deny")}>
              {sending === "deny" ? "Cancelling…" : "Cancel"}
            </button>
          </div>
        </div>

        <p className="mt-5 px-2 text-center text-[13px] leading-snug text-muted">
          You'll be sent back to <strong className="break-all font-semibold text-ink">{client.redirectHost}</strong>.
          {client.loopbackOnly ? " This app runs on your own computer." : ""} You can disconnect it any time from My profiles (or your admin
          account).
        </p>
      </div>
    </Page>
  );
}

function PersonNote({ person }: { person: NonNullable<ConsentLookup["accounts"]["person"]> }) {
  if (person.mcpEnabled) return null;
  return (
    <span className="mt-3 block rounded-[12px] border border-line bg-soft px-3 py-2 text-[14px] leading-snug" data-testid="ai-access-off">
      AI access isn't switched on for your account yet, so the app won't see any results
      {person.leadsTeams > 0 ? " (yours or the teams you lead)" : ""} until the Gifts Profile team turns it on.
    </span>
  );
}

function NothingToGive({ email }: { email: string }) {
  return (
    <p className="rounded-[12px] border border-line bg-soft px-4 py-3 text-[14px] leading-snug">
      <strong className="break-all font-semibold">{email}</strong> can't give this app any of the access it asked for.
    </p>
  );
}

type LinkEvents = { onClick: () => void; onAuxClick: () => void };

function MfaNotice({ email, link }: { email: string; link: LinkEvents }) {
  return (
    <section className="rounded-[16px] border border-line bg-panel p-4" aria-label="Finish signing in" data-testid="mfa-notice">
      <p className="text-[15px] leading-snug">
        You're signed in to the admin as <strong className="break-all font-semibold">{email}</strong>, but you haven't entered your two-step code yet.
      </p>
      <p className="mt-2 text-[15px]">
        <a href={ADMIN_BACK} {...link}>
          Finish two-step sign-in
        </a>, then you'll come straight back here.
      </p>
    </section>
  );
}

function SignInStep({ onSignedIn, adminLink }: { onSignedIn: () => void; adminLink: LinkEvents }) {
  return (
    <section className="mt-7 border-t border-line pt-6" aria-labelledby="connect-sign-in">
      <h2 id="connect-sign-in" className="heading text-[19px] leading-tight">
        Sign in to continue
      </h2>
      <p className="mt-2 text-[15px] leading-snug text-muted">Use the email and password for your Gifts Profile results.</p>
      <PersonSignInForm className="mt-4" initialMode="password" onSignedIn={onSignedIn} />
      <p className="mt-5 border-t border-line pt-4 text-[14px] text-muted">
        Gifts Profile staff? <a href={ADMIN_SIGN_IN} {...adminLink}>Sign in to the admin</a>
      </p>
    </section>
  );
}
